import { Component, Prop, Vue } from 'nuxt-property-decorator';
import {
  EventType,
  FormDto,
  FormType,
  FormState,
  HeaderSectionModel,
  PageDto,
  TranslatableField,
} from '../../../services';

interface HeaderMetaInfo {
  id: string;
  icon: string;
  value: string;
}

interface HeaderButton {
  id: string;
  label: string;
  to: string;
  hierarchy: 'primary' | 'secondary-color';
}

@Component
export default class WebsiteHeaderMixin extends Vue {
  @Prop({ type: Object, required: true }) readonly headerSection!: HeaderSectionModel;
  @Prop({ type: Object, required: true }) readonly name!: TranslatableField;
  @Prop({ type: String, required: true }) readonly startDate!: string;
  @Prop({ type: String, required: true }) readonly endDate!: string;
  @Prop({ type: String, required: true }) readonly type!: EventType;
  @Prop({ type: Object, required: true }) readonly place!: TranslatableField;
  @Prop({ type: Array, required: true }) readonly forms!: FormDto[];
  @Prop({ type: Object, required: true }) readonly registrationPage!: PageDto | undefined;
  @Prop({ type: Object, required: true }) readonly submissionPage!: PageDto | undefined;
  @Prop({ type: Boolean, default: false }) readonly readOnly!: boolean;

  readonly EventType = EventType;

  get imageUrl(): string {
    const imageUrl = this.headerSection.image.url[this.$i18n.locale];
    const imageOriginalUrl = this.headerSection.image.originalUrl[this.$i18n.locale];
    const imageSource = imageUrl || imageOriginalUrl;
    if (!imageSource) return '';
    return `${imageSource}?w=2000&fit=max&auto=format`;
  }

  get venueName(): string {
    return this.transl8(this.place);
  }

  get showRegistrationButton(): boolean {
    const form = this.forms.find(({ type }) => type === FormType.Registration);
    return form ? form.state === FormState.Open || form.state === FormState.FullWithWaitingList : false;
  }

  get showSubmissionButton(): boolean {
    const form = this.forms.find(({ type }) => type === FormType.Submission);
    return form ? form.state === FormState.Open || form.state === FormState.Full : false;
  }

  get headerMetaInfos(): HeaderMetaInfo[] {
    const metaInfos: HeaderMetaInfo[] = [];

    if (this.type !== EventType.Online && this.venueName) {
      metaInfos.push({ id: 'venue', icon: 'location-pin', value: this.venueName });
    }

    metaInfos.push({ id: 'date', icon: 'calendar', value: this.toDateRange(this.startDate, this.endDate) });

    return metaInfos;
  }

  get headerButtons(): HeaderButton[] {
    const buttons: Omit<HeaderButton, 'hierarchy'>[] = [];

    if (this.showRegistrationButton && this.registrationPage) {
      buttons.push({
        id: 'registration',
        label: this.transl8(this.registrationPage.name),
        to: this.localePath({ name: 'slug-registration' }),
      });
    }

    if (this.showSubmissionButton && this.submissionPage) {
      buttons.push({
        id: 'submission',
        label: this.transl8(this.submissionPage.name),
        to: this.localePath({ name: 'slug-submission' }),
      });
    }

    return buttons.map((button, index) => ({ ...button, hierarchy: index === 0 ? 'primary' : 'secondary-color' }));
  }

  get hasButtons(): boolean {
    return this.headerButtons.length > 0;
  }

  get buttonsTabIndex(): number {
    return this.readOnly ? -1 : 0;
  }
}
